import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { selectElearningUser } from 'store/elearning.slice';
import chatApi from 'api/chatApi';
import ChatClient from 'services/ChatClient';

const ChatContext = createContext(null);

export const useChat = () => useContext(ChatContext);

const PRIVACY_KEY = 'chat_privacy_accepted';

function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch {}
}

export default function ChatProvider({ children }) {
  const student = useSelector(selectElearningUser);

  // RC credentials
  const [rcAuth, setRcAuth] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  // DM availability (from rcDm field in elearningUser)
  const hasTech = !!student?.rcDm?.hasTech;
  const hasTeacher = !!student?.rcDm?.hasTeacher;
  const initialTeacherName = student?.rcDm?.teacherName || null;

  // Room IDs
  const [techDmRoomId, setTechDmRoomId] = useState(null);
  const [teacherDmRoomId, setTeacherDmRoomId] = useState(null);
  const [techUsername, setTechUsername] = useState(null);
  const [teacherName, setTeacherName] = useState(initialTeacherName);

  // Unread counts
  const [unreadTech, setUnreadTech] = useState(0);
  const [unreadTeacher, setUnreadTeacher] = useState(0);
  const [unreadChannel, setUnreadChannel] = useState(0);

  // Privacy modal
  const [privacyAccepted, setPrivacyAccepted] = useState(
    () => localStorage.getItem(PRIVACY_KEY) === 'true'
  );

  // Active room tracking
  const activeRoomRef = useRef(null);
  const techDmRoomIdRef = useRef(null);
  const teacherDmRoomIdRef = useRef(null);
  const clientRef = useRef(null);
  const messageListenersRef = useRef(new Set());

  // Extract RC auth from elearningUser when available
  useEffect(() => {
    if (student?.rcAuth?.rcAuthToken) {
      const data = {
        authToken: student.rcAuth.rcAuthToken,
        userId: student.rcAuth.rcUserId,
        username: student.rcAuth.rcUsername,
        channelId: student.rcCourse?.channelId || student.course?.rcChannelId || null,
        channelName: student.rcCourse?.channelName || student.course?.rcChannelName || null,
        channelFname: student.rcCourse?.channelFname || student.course?.rcChannelFname || null,
      };
      setRcAuth(data);
    }
  }, [student?.rcAuth?.rcAuthToken, student?.rcCourse?.channelId]);

  // Update teacherName from rcDm
  useEffect(() => {
    if (student?.rcDm?.teacherName) {
      setTeacherName(student.rcDm.teacherName);
    }
  }, [student?.rcDm?.teacherName]);

  // Fallback: login via registrationCode + phone
  const loginToRcFallback = useCallback(async () => {
    if (!student?.registrationCode || !student?.tel) return null;
    try {
      const res = await chatApi.studentLogin(student.registrationCode, student.tel);
      const d = res?.data;
      if (d?.authToken) {
        const data = {
          authToken: d.authToken,
          userId: d.userId,
          username: d.username,
          channelId: d.course?.channelId,
          channelName: d.course?.channelName,
          channelFname: d.course?.channelFname,
        };
        setRcAuth(data);
        return data;
      }
    } catch (err) {
      console.error('RC fallback login failed:', err);
    }
    return null;
  }, [student?.registrationCode, student?.tel]);

  const ensureAuth = useCallback(async () => {
    if (rcAuth?.authToken) return rcAuth;
    return loginToRcFallback();
  }, [rcAuth, loginToRcFallback]);

  // Open DM with tech (RC Token headers + body fallback)
  const openTechDm = useCallback(async () => {
    if (!rcAuth?.authToken) return null;
    try {
      const res = await chatApi.openDmTech({
        rcAuthToken: rcAuth.authToken,
        rcUserId: rcAuth.userId,
        registrationCode: student?.registrationCode,
        phone: student?.tel,
      });
      const room = res?.data?.room;
      if (room?._id) {
        setTechDmRoomId(room._id);
        techDmRoomIdRef.current = room._id;
        setTechUsername(res?.data?.techUsername);
        if (clientRef.current) clientRef.current.subscribeRoom(room._id);
        return room._id;
      }
    } catch (err) {
      console.error('Open tech DM failed:', err);
    }
    return null;
  }, [rcAuth?.authToken, rcAuth?.userId, student?.registrationCode, student?.tel]);

  // Open DM with teacher (RC Token headers + body fallback)
  const openTeacherDm = useCallback(async () => {
    if (!rcAuth?.authToken) return null;
    try {
      const res = await chatApi.openDmTeacher({
        rcAuthToken: rcAuth.authToken,
        rcUserId: rcAuth.userId,
        registrationCode: student?.registrationCode,
        phone: student?.tel,
      });
      const room = res?.data?.room;
      if (room?._id) {
        setTeacherDmRoomId(room._id);
        teacherDmRoomIdRef.current = room._id;
        if (res?.data?.teacherFullName || res?.data?.teacherName) setTeacherName(res.data.teacherFullName || res.data.teacherName);
        if (clientRef.current) clientRef.current.subscribeRoom(room._id);
        return room._id;
      }
    } catch (err) {
      console.error('Open teacher DM failed:', err);
    }
    return null;
  }, [rcAuth?.authToken, rcAuth?.userId, student?.registrationCode, student?.tel]);

  // Accept privacy
  const acceptPrivacy = useCallback(() => {
    setPrivacyAccepted(true);
    localStorage.setItem(PRIVACY_KEY, 'true');
  }, []);

  // Gọi RC API để đánh dấu toàn bộ room là đã đọc.
  // Best-effort: không chặn UI, lỗi sẽ được nuốt vì counter local đã clear.
  const markRoomAsRead = useCallback(
    async (roomId) => {
      if (!roomId || !rcAuth?.authToken) return;
      try {
        await chatApi.markRoomAsRead(roomId, rcAuth.authToken, rcAuth.userId);
      } catch (err) {
        console.error('markRoomAsRead failed:', err);
      }
    },
    [rcAuth?.authToken, rcAuth?.userId]
  );

  // Set active room (suppress unread + mark as read on server)
  const setActiveRoom = useCallback(
    (roomId) => {
      activeRoomRef.current = roomId;
      if (roomId) {
        setUnreadTech((prev) => (roomId === techDmRoomId ? 0 : prev));
        setUnreadTeacher((prev) => (roomId === teacherDmRoomId ? 0 : prev));
        setUnreadChannel((prev) => (roomId === rcAuth?.channelId ? 0 : prev));
        markRoomAsRead(roomId);
      }
    },
    [techDmRoomId, teacherDmRoomId, rcAuth?.channelId, markRoomAsRead]
  );

  const addMessageListener = useCallback((listener) => {
    messageListenersRef.current.add(listener);
    return () => messageListenersRef.current.delete(listener);
  }, []);

  const fetchUnreadCounters = useCallback(
    async (roomId) => {
      if (!rcAuth?.authToken || !roomId) return 0;
      try {
        const res = await chatApi.getUnreadCounters(roomId, rcAuth.authToken, rcAuth.userId);
        return res?.data?.unreads || 0;
      } catch {
        return 0;
      }
    },
    [rcAuth?.authToken, rcAuth?.userId]
  );

  // Connect WebSocket
  useEffect(() => {
    if (!rcAuth?.authToken) return;

    const client = new ChatClient(rcAuth.authToken);
    clientRef.current = client;

    client.onConnected = () => {
      setIsConnected(true);
      if (rcAuth.channelId) client.subscribeRoom(rcAuth.channelId);
      if (techDmRoomId) client.subscribeRoom(techDmRoomId);
      if (teacherDmRoomId) client.subscribeRoom(teacherDmRoomId);
    };

    client.onMessage = (roomId, msg) => {
      messageListenersRef.current.forEach((fn) => fn(roomId, msg));

      if (roomId !== activeRoomRef.current && msg?.u?._id !== rcAuth.userId) {
        if (roomId === techDmRoomIdRef.current) {
          playNotificationSound();
          setUnreadTech((prev) => prev + 1);
        } else if (roomId === teacherDmRoomIdRef.current) {
          playNotificationSound();
          setUnreadTeacher((prev) => prev + 1);
        } else if (roomId === rcAuth.channelId) {
          setUnreadChannel((prev) => prev + 1);
        }
      }
    };

    client.connect();

    return () => {
      client.disconnect();
      clientRef.current = null;
      setIsConnected(false);
    };
  }, [rcAuth?.authToken]);

  // Auto-open DM rooms khi kết nối để nhận thông báo ngay từ đầu
  useEffect(() => {
    if (!isConnected || !rcAuth?.authToken) return;

    const initDmRooms = async () => {
      if (hasTech && !techDmRoomIdRef.current) {
        await openTechDm();
      }
      if (hasTeacher && !teacherDmRoomIdRef.current) {
        await openTeacherDm();
      }
    };

    initDmRooms();
  }, [isConnected, rcAuth?.authToken, hasTech, hasTeacher, openTechDm, openTeacherDm]);

  // Fetch initial unread counters (dm/counters only works for DM rooms, not channels)
  useEffect(() => {
    if (!isConnected || !rcAuth?.authToken) return;
    const fetchAll = async () => {
      if (techDmRoomId) {
        const count = await fetchUnreadCounters(techDmRoomId);
        setUnreadTech(count);
      }
      if (teacherDmRoomId) {
        const count = await fetchUnreadCounters(teacherDmRoomId);
        setUnreadTeacher(count);
      }
    };
    fetchAll();
  }, [isConnected, rcAuth?.authToken, techDmRoomId, teacherDmRoomId]);

  const totalUnread = unreadTech + unreadTeacher + unreadChannel;
  const chatReady = !!rcAuth?.authToken;

  const value = {
    rcAuth,
    isConnected,
    chatReady,
    ensureAuth,

    // DM availability
    hasTech,
    hasTeacher,

    // Rooms
    channelId: rcAuth?.channelId,
    channelName: rcAuth?.channelName,
    channelFname: rcAuth?.channelFname,
    techDmRoomId,
    teacherDmRoomId,
    techUsername,
    teacherName,
    openTechDm,
    openTeacherDm,

    // Unread
    unreadTech,
    unreadTeacher,
    unreadChannel,
    totalUnread,
    setActiveRoom,

    // Privacy
    privacyAccepted,
    acceptPrivacy,

    // Messages
    addMessageListener,
    fetchUnreadCounters,
    markRoomAsRead,

    clientRef,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
